import { FeedbackInputOutput, ImageTag } from '@wix/user-feedback-opener';
import { isEmpty, path, pipe, pluck } from 'ramda';
import React, { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Loader, Text, TextButton } from '../../../components/common';
import { HomeButtonName, useTrackMediaHomeAnalytics } from '../../../utils/hooks/bi/useTrackMediaHomeAnalytics';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { useUserFeedback, useUserFeedbackOnClose } from '../../../utils/hooks/useUserFeedback';
import { LoadingSection } from '../LoadingSection';
import { HorizontalSection } from '../../../components/common/HorizontalSection';
import { Recommended } from './Recommended';
import styles from './RecommendedSection.scss';
import { IOuter } from './RecommendedSection.types';

const getTopicIds = pipe<FeedbackInputOutput, ImageTag[], string[]>(
  path(['questionAnswer', 'output', 'tags']),
  pluck('id'),
);

export const RecommendedSection: FC<IOuter> = ({
  isLoading,
  openExpandedView,
  buyItem,
  onItemClick,
  onItemDoubleClick,
  isItemSelected,
  selectedItems,
  isSecure,
  beginDrag,
  publicMediaCurrentCategory,
  items,
  isRecommendedCategoriesLoading,
  onItemRightClick,
  recommendedCategories,
  loadingError,
  selectRecommendedCategories,
  dataHook,
  currentFolder,
}) => {
  const { trackRecommendedButtonClick } = useTrackMediaHomeAnalytics();
  const { value: didCategoriesChange, setTrue: markCategoriesAsChanged } = useBoolean();
  const { value: shouldRenderSectionLoader, setFalse: renderUpdatingLoader } = useBoolean(true);

  useEffect(() => {
    if (!didCategoriesChange) {
      return;
    }

    renderUpdatingLoader();
  }, [recommendedCategories, didCategoriesChange]);

  const hasSavedRecommendedCategories = !isEmpty(recommendedCategories);

  const { openUserFeedbackForRecommendedTopics } = useUserFeedback();

  const handleNewTopicsChosen = useCallback(
    ({ closeEventPayload: { questionResult } }) => {
      const topics = getTopicIds(questionResult);

      markCategoriesAsChanged();
      selectRecommendedCategories(topics);
    },
    [markCategoriesAsChanged, selectRecommendedCategories],
  );
  useUserFeedbackOnClose(handleNewTopicsChosen);

  const handleConfigurationClick = useCallback(() => {
    openUserFeedbackForRecommendedTopics(recommendedCategories);
    trackRecommendedButtonClick(recommendedCategories, HomeButtonName.Settings);
  }, [openUserFeedbackForRecommendedTopics, trackRecommendedButtonClick, recommendedCategories]);

  const handleNavigationLeftButtonClick = useCallback(
    () => trackRecommendedButtonClick(recommendedCategories, HomeButtonName.NavLeft),
    [trackRecommendedButtonClick, recommendedCategories],
  );

  const handleNavigationRightButtonClick = useCallback(
    () => trackRecommendedButtonClick(recommendedCategories, HomeButtonName.NavRight),
    [trackRecommendedButtonClick, recommendedCategories],
  );

  const isShowingLoader = isRecommendedCategoriesLoading || isLoading;

  const recommendedItems = !loadingError && (
    <HorizontalSection
      header={
        <>
          <Text
            weight="bold"
            size="medium"
            className={styles.recommendedItemsHeaderText}
            tagName="div"
            dataHook="recommended-section-header"
          >
            <FormattedMessage
              id={
                hasSavedRecommendedCategories
                  ? 'media-home.recommendation-section.items.recommended.header'
                  : 'media-home.recommendation-section.items.popular.header'
              }
            />
          </Text>

          <Text weight="thin" size="small" tagName="div">
            <FormattedMessage
              id={
                hasSavedRecommendedCategories
                  ? 'media-home.recommendation-section.items.recommended.subtitle'
                  : 'media-home.recommendation-section.items.popular.subtitle'
              }
              values={{
                editTopics: (
                  <TextButton
                    size="small"
                    weight="normal"
                    onClick={handleConfigurationClick}
                    dataHook="edit-topics-button"
                  >
                    <FormattedMessage
                      id={
                        hasSavedRecommendedCategories
                          ? 'media-home.recommendation-section.items.recommended.subtitle.edit'
                          : 'media-home.recommendation-section.items.popular.subtitle.edit'
                      }
                    />
                  </TextButton>
                ),
              }}
            />
          </Text>
        </>
      }
    >
      {!isEmpty(items) && (
        <div className={styles.recommendedSectionItems}>
          <Recommended
            dataHook={dataHook}
            openExpandedView={openExpandedView}
            buyItem={buyItem}
            onItemClick={onItemClick}
            onItemDoubleClick={onItemDoubleClick}
            isItemSelected={isItemSelected}
            selectedItems={selectedItems}
            isSecure={isSecure}
            beginDrag={beginDrag}
            publicMediaCurrentCategory={publicMediaCurrentCategory}
            items={items}
            onNavLeftClick={handleNavigationLeftButtonClick}
            onNavRightClick={handleNavigationRightButtonClick}
            isLoading={isLoading}
            onItemRightClick={onItemRightClick}
            data-hook="items"
            currentFolder={currentFolder}
          />
        </div>
      )}
    </HorizontalSection>
  );

  const loaderType = shouldRenderSectionLoader ? (
    <HorizontalSection>
      <LoadingSection
        headerText={<FormattedMessage id="media-home.recommendation-section.items.recommended.header" />}
      />
    </HorizontalSection>
  ) : (
    <HorizontalSection
      header={
        <Text weight="bold" size="medium" className={styles.loaderHeader}>
          <FormattedMessage id="media-home.recommendation-section.items.recommended.header" />
        </Text>
      }
    >
      <div className={styles.loader} data-hook="updating-recommended-loader">
        <Loader />
        <Text weight="normal" size="small" className={styles.loaderText}>
          <FormattedMessage id="media-home.recommendation-section.loader-text" />
        </Text>
      </div>
    </HorizontalSection>
  );

  return isShowingLoader ? loaderType : recommendedItems;
};
